// frontend/src/components/WorkflowCenter.jsx
// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "../design-system/components";
import { useAdvisor } from "../AdvisorContext";
import "../workflowCenter.css";
import ContextualExplainDrawer from "./ai/ContextualExplainDrawer";
import WorkflowIntelligencePanel from "./workflow/WorkflowIntelligencePanel";

const REQUEST_TIMEOUT_MS = 30000;

const WORKFLOW_EXPLAIN_PROMPTS = [
  "Explain this workflow.",
  "What is blocking it?",
  "What is it waiting on?",
  "What changed in this workflow?",
];

const STATUS_LABELS = {
  open: "Open",
  pending: "Pending",
  passed: "Passed",
  ready_for_validation: "Ready for Validation",
  in_progress: "In Progress",
  validated: "Validated",
  closed: "Closed",
  created: "Created",
  queued: "Queued",
  waiting: "Waiting",
  assigned: "Assigned",
  executing: "Executing",
  awaiting_approval: "Awaiting Approval",
  awaiting_validation: "Awaiting Validation",
  blocked: "Blocked",
  completed: "Completed",
  cancelled: "Cancelled",
  failed: "Failed",
};

const STATUS_TONES = {
  open: "info",
  pending: "warn",
  passed: "ok",
  ready_for_validation: "warn",
  in_progress: "active",
  validated: "ok",
  closed: "ok",
  created: "neutral",
  queued: "info",
  waiting: "warn",
  assigned: "info",
  executing: "active",
  awaiting_approval: "warn",
  awaiting_validation: "warn",
  blocked: "danger",
  completed: "ok",
  cancelled: "neutral",
  failed: "danger",
};

function cleanStatus(value) {
  return String(value || "").trim().toLowerCase();
}

function formatStatus(value) {
  const status = cleanStatus(value);
  return STATUS_LABELS[status] || (status ? status.replace(/_/g, " ") : "Unknown");
}

function statusTone(value) {
  return STATUS_TONES[cleanStatus(value)] || "neutral";
}

function safeNumber(value, fallback = 0) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
}

function formatDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString();
}

function formatSource(value) {
  const source = String(value || "").trim();
  if (!source) return "—";
  if (source === "google_workspace") return "Google Workspace";
  return source.replace(/_/g, " ").replace(/\b\w/g, (match) => match.toUpperCase());
}

function getTenantId(userProfile = {}) {
  return (
    userProfile?.tenant_id ||
    userProfile?.tenantId ||
    userProfile?.app_metadata?.tenant_id ||
    userProfile?.app_metadata?.tenantId ||
    process.env.REACT_APP_TENANT_ID ||
    ""
  );
}

function getUserId(userProfile = {}) {
  return (
    userProfile?.user_id ||
    userProfile?.userId ||
    userProfile?.id ||
    userProfile?.app_metadata?.user_id ||
    userProfile?.app_metadata?.userId ||
    process.env.REACT_APP_USER_ID ||
    ""
  );
}

function StatCard({ label, value, footnote, tone = "default" }) {
  return (
    <div className={`workflow-stat-card is-${tone}`}>
      <span>{label}</span>
      <strong>{value}</strong>
      {footnote ? <small>{footnote}</small> : null}
    </div>
  );
}

function StatusBadge({ value }) {
  const status = cleanStatus(value);
  return <span className={`workflow-status-badge is-${statusTone(status)}`}>{formatStatus(status)}</span>;
}

function EmptyState({ title, description }) {
  return (
    <div className="workflow-empty-state">
      <h3>{title}</h3>
      <p>{description}</p>
    </div>
  );
}

function normalizeApiTaskPayload(payload) {
  if (!payload) return null;
  if (payload.task && typeof payload.task === "object") return payload.task;
  if (payload.item && typeof payload.item === "object") return payload.item;
  if (payload.data && typeof payload.data === "object") return payload.data;
  return typeof payload === "object" ? payload : null;
}

function linkedRemediationLinks(links = []) {
  return (Array.isArray(links) ? links : []).filter((link) => {
    const table = String(link?.linked_table || "").toLowerCase();
    const type = String(link?.link_type || "").toLowerCase();
    return table === "remediation_tasks" || type === "remediation_task";
  });
}

function metadataDict(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : {};
}

function getWorkflowRecovery(task = {}) {
  const metadata = metadataDict(task?.task_metadata);
  return metadataDict(metadata.workflow_recovery || metadata.workflowRecovery);
}

function getWorkflowMetadata(task = {}) {
  const metadata = metadataDict(task?.task_metadata);
  return metadataDict(metadata.workflow || metadata.workflow_metadata || metadata.workflowMetadata);
}

function hasObjectValues(value = {}) {
  return Object.keys(metadataDict(value)).length > 0;
}

function formatWorkflowSentence(value = "") {
  const raw = String(value || "").trim();
  if (!raw) return "Workflow sync recorded.";

  return raw
    .replace(/awaiting_validation/g, "Awaiting Validation")
    .replace(/awaiting_approval/g, "Awaiting Approval")
    .replace(/ready_for_validation/g, "Ready for Validation")
    .replace(/in_progress/g, "In Progress")
    .replace(/\bfailed\b/g, "Failed")
    .replace(/\bcompleted\b/g, "Completed")
    .replace(/\bexecuting\b/g, "Executing")
    .replace(/\bqueued\b/g, "Queued")
    .replace(/\bassigned\b/g, "Assigned")
    .replace(/\bblocked\b/g, "Blocked");
}

const WORKFLOW_EVENT_LABELS = {
  approval_recorded: "Approval Recorded",
  dependency_evaluation: "Dependency Evaluation",
  dependencies_evaluated: "Dependencies Evaluated",
  dependency_evaluated: "Dependency Evaluated",
  remediation_validation_failed: "Validation Failed",
  remediation_validation_passed: "Validation Passed",
  remediation_validation_synced: "Validation Synced",
  validation_failed: "Validation Failed",
  validation_passed: "Validation Passed",
  workflow_advanced: "Workflow Advanced",
  workflow_approved: "Workflow Approved",
  workflow_blocked: "Workflow Blocked",
  workflow_completed: "Workflow Completed",
  workflow_created: "Workflow Created",
  workflow_failed: "Workflow Failed",
  workflow_retry: "Workflow Retried",
  workflow_retry_queued: "Workflow Retry Queued",
  workflow_step_completed: "Step Completed",
  workflow_step_failed: "Step Failed",
  workflow_validation_failed: "Validation Failed",
  workflow_validation_passed: "Validation Passed",
};

function titleCaseWords(value = "") {
  return String(value || "")
    .trim()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

function formatWorkflowHistoryTitle(item = {}) {
  const eventType = String(item?.event_type || item?.eventType || "").trim();
  if (eventType) {
    return WORKFLOW_EVENT_LABELS[eventType] || titleCaseWords(eventType);
  }

  const fromStatus = item?.from_status || item?.fromStatus || "";
  const toStatus = item?.to_status || item?.toStatus || "";
  if (fromStatus || toStatus) {
    return `${formatStatus(fromStatus || "unknown")} → ${formatStatus(toStatus || "unknown")}`;
  }

  return "Workflow Event";
}

function formatWorkflowHistoryDetail(item = {}) {
  const message = item?.message || item?.transition_reason || item?.transitionReason || "";
  if (message) return formatWorkflowSentence(message);

  const eventType = String(item?.event_type || item?.eventType || "").trim();
  const fromStatus = item?.from_status || item?.fromStatus || "";
  const toStatus = item?.to_status || item?.toStatus || "";

  if (eventType === "workflow_retry") {
    return "Workflow was returned to the queue for another remediation pass.";
  }
  if (eventType.includes("validation") && eventType.includes("failed")) {
    return "Validation failed against the latest source evidence.";
  }
  if (eventType.includes("validation") && eventType.includes("passed")) {
    return "Validation passed against the latest source evidence.";
  }
  if (eventType.includes("dependency")) {
    return "Workflow dependencies were evaluated for readiness.";
  }
  if (eventType.includes("approval")) {
    return "Workflow approval activity was recorded.";
  }
  if (fromStatus || toStatus) {
    return `Workflow moved from ${formatStatus(fromStatus || "unknown")} to ${formatStatus(toStatus || "unknown")}.`;
  }

  return "Workflow event recorded.";
}

function workflowHistoryTone(item = {}) {
  const eventType = String(item?.event_type || item?.eventType || "").toLowerCase();
  const toStatus = cleanStatus(item?.to_status || item?.toStatus || "");
  const status = cleanStatus(item?.status || "");

  if (eventType.includes("failed") || toStatus === "failed" || status === "failed") return "failed";
  if (eventType.includes("blocked") || toStatus === "blocked" || status === "blocked") return "blocked";
  if (eventType.includes("retry") || toStatus === "queued" || status === "queued") return "queued";
  if (eventType.includes("approval") || toStatus === "awaiting_approval") return "awaiting_approval";
  if (eventType.includes("validation") || toStatus === "awaiting_validation") return "awaiting_validation";
  if (eventType.includes("completed") || toStatus === "completed" || status === "completed") return "completed";
  if (eventType.includes("dependency")) return "pending";
  if (toStatus) return toStatus;
  return "created";
}

function syncBadgeStyle(value = "") {
  const tone = statusTone(value);
  const color =
    tone === "danger"
      ? "#ff7b7b"
      : tone === "warn"
      ? "#ffcc66"
      : tone === "ok"
      ? "#4fd28e"
      : tone === "active" || tone === "info"
      ? "#6aa8ff"
      : "#a8b3c7";

  return {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 28,
    borderRadius: 999,
    padding: "0 10px",
    border: `1px solid color-mix(in srgb, ${color}, transparent 62%)`,
    color,
    background: `color-mix(in srgb, ${color}, transparent 90%)`,
    fontSize: 11,
    fontWeight: 800,
    lineHeight: 1,
    whiteSpace: "nowrap",
  };
}

function syncSummary(sync = {}) {
  const transition = metadataDict(sync.transition);
  const currentStep = metadataDict(sync.current_step);
  const statusValue =
    sync.validation_result ||
    transition.workflow_status ||
    sync.target_workflow_status ||
    currentStep.workflow_status ||
    sync.action ||
    "linked";

  return {
    statusValue,
    action: formatStatus(statusValue),
    message: formatWorkflowSentence(sync?.message || sync?.reason || "No workflow sync message recorded."),
  };
}

function WorkflowSyncBlock({ title, sync, historical = false }) {
  const payload = metadataDict(sync);
  const summary = syncSummary(payload);
  const transition = metadataDict(payload.transition);
  const currentStep = metadataDict(payload.current_step);
  const targetStatus = payload.target_workflow_status || payload.targetWorkflowStatus || "";
  const workflowStatus = transition.workflow_status || transition.workflowStatus || "";
  const stepStatus =
    transition.current_step_status ||
    transition.currentStepStatus ||
    currentStep.step_status ||
    currentStep.stepStatus ||
    "";
  const currentStepName =
    currentStep.step_name ||
    currentStep.stepName ||
    currentStep.step_key ||
    currentStep.stepKey ||
    "";

  return (
    <div className={`workflow-sync-block ${historical ? "is-historical" : ""}`}>
      <div className="workflow-sync-block-head">
        <strong>{title}</strong>
        <div className="workflow-sync-status-group">
          {historical ? <span style={syncBadgeStyle("neutral")}>Historical</span> : null}
          <span style={syncBadgeStyle(summary.statusValue)}>{summary.action}</span>
        </div>
      </div>
      <p>{historical ? `Previous: ${summary.message}` : summary.message}</p>
      {historical ? <small>Historical sync captured before the latest workflow recovery.</small> : null}
      <div className="workflow-sync-meta">
        {payload.validation_result ? (
          <span>
            Result: <strong>{formatStatus(payload.validation_result)}</strong>
          </span>
        ) : null}
        {targetStatus ? (
          <span>
            Target: <strong>{formatStatus(targetStatus)}</strong>
          </span>
        ) : null}
        {workflowStatus ? (
          <span>
            Workflow: <strong>{formatStatus(workflowStatus)}</strong>
          </span>
        ) : null}
        {stepStatus ? (
          <span>
            Step: <strong>{formatStatus(stepStatus)}</strong>
          </span>
        ) : null}
        {currentStepName ? (
          <span>
            Current: <strong>{formatStatus(currentStepName)}</strong>
          </span>
        ) : null}
      </div>
      {payload.synced_at ? <small>Synced {formatDateTime(payload.synced_at)}</small> : null}
    </div>
  );
}

function WorkflowRecoveryBlock({ recovery = {}, workflow = {}, task = {} }) {
  const payload = metadataDict(recovery);
  if (!hasObjectValues(payload)) return null;

  const retry = metadataDict(payload.retry);
  const workflowStatus =
    retry.workflow_status ||
    retry.workflowStatus ||
    workflow.workflow_status ||
    workflow.workflowStatus ||
    workflow.status ||
    "queued";
  const currentStepStatus =
    retry.current_step_status ||
    retry.currentStepStatus ||
    workflow.current_step_status ||
    workflow.currentStepStatus ||
    "";
  const currentStepKey =
    retry.current_step_key ||
    retry.currentStepKey ||
    workflow.current_step_key ||
    workflow.currentStepKey ||
    "";
  const previousTaskStatus = payload.previous_task_status || payload.previousTaskStatus || "";
  const nextTaskStatus = payload.next_task_status || payload.nextTaskStatus || task?.status || "";
  const previousValidationStatus =
    payload.previous_validation_status || payload.previousValidationStatus || "";
  const nextValidationStatus =
    payload.next_validation_status || payload.nextValidationStatus || task?.validation_status || "";
  const recoveredAt =
    payload.recovered_at || payload.recoveredAt || workflow.last_recovered_at || workflow.lastRecoveredAt;

  return (
    <div className="workflow-sync-block is-recovered workflow-recovery-summary">
      <div className="workflow-sync-block-head">
        <strong>Workflow Recovery</strong>
        <span style={syncBadgeStyle("passed")}>Recovered</span>
      </div>
      <p>Workflow recovered to active work.</p>
      {recoveredAt ? <small>Recovered {formatDateTime(recoveredAt)}</small> : null}

      <div className="workflow-sync-meta">
        {workflowStatus ? (
          <span>
            Workflow: <strong>{formatStatus(workflowStatus)}</strong>
          </span>
        ) : null}
        {currentStepStatus ? (
          <span>
            Step: <strong>{formatStatus(currentStepStatus)}</strong>
          </span>
        ) : null}
        {currentStepKey ? (
          <span>
            Current: <strong>{formatStatus(currentStepKey)}</strong>
          </span>
        ) : null}
        {previousTaskStatus ? (
          <span>
            Previous task: <strong>{formatStatus(previousTaskStatus)}</strong>
          </span>
        ) : null}
        {nextTaskStatus ? (
          <span>
            Current task: <strong>{formatStatus(nextTaskStatus)}</strong>
          </span>
        ) : null}
        {previousValidationStatus ? (
          <span>
            Previous validation: <strong>{formatStatus(previousValidationStatus)}</strong>
          </span>
        ) : null}
        {nextValidationStatus ? (
          <span>
            Current validation: <strong>{formatStatus(nextValidationStatus)}</strong>
          </span>
        ) : null}
      </div>
    </div>
  );
}


function WorkflowRecoveryLifecycleTimeline({
  task = {},
  recovery = {},
  workflow = {},
  validationSync = {},
  lastValidation = {},
}) {
  const recoveryPayload = metadataDict(recovery);
  if (!hasObjectValues(recoveryPayload)) return null;

  const retry = metadataDict(recoveryPayload.retry);
  const validationPayload = metadataDict(validationSync);
  const lastValidationPayload = metadataDict(lastValidation);
  const recoveredAt =
    recoveryPayload.recovered_at ||
    recoveryPayload.recoveredAt ||
    workflow.last_recovered_at ||
    workflow.lastRecoveredAt;

  const validationFailedAt =
    validationPayload.synced_at ||
    validationPayload.syncedAt ||
    lastValidationPayload.executed_at ||
    lastValidationPayload.executedAt ||
    lastValidationPayload.created_at ||
    lastValidationPayload.createdAt;

  const failureReason =
    validationPayload.reason ||
    validationPayload.message ||
    lastValidationPayload.reason ||
    lastValidationPayload.failure_reason ||
    lastValidationPayload.validation_reason ||
    "Source evidence still failed validation.";

  const recoveryReason =
    recoveryPayload.reason ||
    "Workflow was recovered and the task was returned to active work.";

  const previousTaskStatus = recoveryPayload.previous_task_status || recoveryPayload.previousTaskStatus || "";
  const nextTaskStatus = recoveryPayload.next_task_status || recoveryPayload.nextTaskStatus || task?.status || "";
  const previousValidationStatus =
    recoveryPayload.previous_validation_status || recoveryPayload.previousValidationStatus || "";
  const nextValidationStatus =
    recoveryPayload.next_validation_status || recoveryPayload.nextValidationStatus || task?.validation_status || "";
  const workflowStatus =
    retry.workflow_status ||
    retry.workflowStatus ||
    workflow.workflow_status ||
    workflow.workflowStatus ||
    workflow.status ||
    "queued";
  const currentStepStatus =
    retry.current_step_status ||
    retry.currentStepStatus ||
    workflow.current_step_status ||
    workflow.currentStepStatus ||
    "";

  const steps = [
    {
      label: "Validation failed",
      tone: "failed",
      title: "Source evidence still failed validation.",
      timestamp: validationFailedAt,
      detail: failureReason,
      transition: previousValidationStatus
        ? `Validation moved through ${formatStatus(previousValidationStatus)}.`
        : "Validation failure was recorded before recovery.",
    },
    {
      label: "Recovered",
      tone: "passed",
      title: "Workflow recovered to active work.",
      timestamp: recoveredAt,
      detail: recoveryReason,
      transition:
        previousTaskStatus || nextTaskStatus
          ? `${formatStatus(previousTaskStatus)} → ${formatStatus(nextTaskStatus)}`
          : "Task returned to active work.",
    },
    {
      label: "Pending",
      tone: "pending",
      title: "Validation reset for the next remediation pass.",
      timestamp: recoveredAt,
      detail: "The prior failure remains in history, while current validation is pending re-check.",
      transition:
        previousValidationStatus || nextValidationStatus
          ? `${formatStatus(previousValidationStatus)} → ${formatStatus(nextValidationStatus)}`
          : "Validation reset to Pending.",
    },
    {
      label: "Current",
      tone: "passed",
      title: "Current lifecycle state.",
      timestamp: recoveredAt,
      detail: `Task is ${formatStatus(task?.status || nextTaskStatus)} with ${formatStatus(
        task?.validation_status || nextValidationStatus || "pending"
      )} validation.`,
      transition: `Workflow: ${formatStatus(workflowStatus)}${
        currentStepStatus ? ` · Step: ${formatStatus(currentStepStatus)}` : ""
      }`,
    },
  ];

  return (
    <div className="workflow-sync-block is-recovered workflow-recovery-lifecycle">
      <div className="workflow-sync-block-head">
        <strong>Recovery Lifecycle</strong>
        <span style={syncBadgeStyle("passed")}>Recovered</span>
      </div>
      <p>Validation failed → recovery recorded → task returned to active work.</p>
      <small>
        Current state is {formatStatus(task?.status || nextTaskStatus)} with{" "}
        {formatStatus(task?.validation_status || nextValidationStatus || "pending")} validation.
        Historical failure evidence remains preserved below.
      </small>

      <div className="workflow-recovery-steps">
        {steps.map((step, index) => (
          <div
            key={`${step.label}-${index}`}
            className="workflow-sync-block workflow-recovery-step"
            style={{
              borderLeft: `3px solid ${
                statusTone(step.tone) === "danger"
                  ? "#ff7b7b"
                  : statusTone(step.tone) === "warn"
                  ? "#ffcc66"
                  : "#4fd28e"
              }`,
            }}
          >
            <div
              className="workflow-sync-block-head"
              style={{ alignItems: "center", gap: 8, flexWrap: "wrap" }}
            >
              <strong>
                Step {index + 1} · <span style={syncBadgeStyle(step.tone)}>{step.label}</span>{" "}
                {step.title}
              </strong>
              {step.timestamp ? <small>{formatDateTime(step.timestamp)}</small> : null}
            </div>
            <p>{step.detail}</p>
            <div className="workflow-sync-meta">
              <span>
                <strong>{step.transition}</strong>
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function LinkedWorkPanel({ linkedTasks = [], loading = false, onOpenLinkedTask }) {
  return (
    <div className="workflow-card-block workflow-linked-work-card">
      <div className="workflow-card-title-row">
        <h3>Linked Work</h3>
        {loading ? <span className="workflow-muted">Loading linked tasks…</span> : null}
      </div>

      {!loading && linkedTasks.length === 0 ? (
        <p className="workflow-muted">No linked remediation task has been attached to this workflow yet.</p>
      ) : null}

      <div className="workflow-linked-work-list">
        {linkedTasks.map(({ link, task, error }) => {
          const metadata = metadataDict(task?.task_metadata);
          const workflow = getWorkflowMetadata(task);
          const workflowRecovery = getWorkflowRecovery(task);
          const workflowTaskState = metadataDict(metadata.workflow_task_state);
          const workflowValidation = metadataDict(metadata.workflow_validation);
          const lastValidation = metadataDict(metadata.last_validation);
          const hasWorkflowRecovery = hasObjectValues(workflowRecovery);
          const title = task?.title || link?.linked_label || "Linked remediation task";

          return (
            <div key={link?.id || link?.linked_id || title} className="workflow-linked-task-card">
              <div className="workflow-linked-task-header">
                <div>
                  <strong>{title}</strong>
                  <span>
                    {formatSource(task?.source || link?.metadata?.source)} ·{" "}
                    {task?.resource_id || link?.metadata?.resource_id || link?.linked_id || "resource"}
                  </span>
                </div>
                <span
                  style={{
                    display: "inline-flex",
                    gap: 8,
                    alignItems: "center",
                    flexWrap: "wrap",
                    justifyContent: "flex-end",
                  }}
                >
                  <StatusBadge value={task?.status || "linked"} />
                  {task?.id && typeof onOpenLinkedTask === "function" ? (
                    <button
                      type="button"
                      className="workflow-linked-task-open-button"
                      onClick={() => onOpenLinkedTask({ task, link })}
                    >
                      Open Task
                    </button>
                  ) : null}
                </span>
              </div>

              {error ? (
                <p className="workflow-alert is-danger">Unable to load linked task details: {error}</p>
              ) : null}

              <div className="workflow-linked-task-metrics">
                <span>
                  Owner <strong>{task?.owner_name || task?.owner_user_id || "Unassigned"}</strong>
                </span>
                <span>
                  Rule <strong>{task?.rule_id || link?.metadata?.rule_id || "—"}</strong>
                </span>
                <span>
                  Control <strong>{task?.control_id || link?.metadata?.control_id || "—"}</strong>
                </span>
                <span>
                  Validation <StatusBadge value={task?.validation_status || "pending"} />
                </span>
              </div>

              {lastValidation?.reason ? (
                <div className="workflow-linked-validation-note">
                  <strong>Last validation</strong>
                  <p>{lastValidation.reason}</p>
                </div>
              ) : null}

              {hasWorkflowRecovery ? (
                <>
                  <WorkflowRecoveryBlock recovery={workflowRecovery} workflow={workflow} task={task} />
                  <WorkflowRecoveryLifecycleTimeline
                    task={task}
                    recovery={workflowRecovery}
                    workflow={workflow}
                    validationSync={workflowValidation}
                    lastValidation={lastValidation}
                  />
                </>
              ) : null}

              <div className="workflow-linked-sync-grid">
                {Object.keys(workflowTaskState).length ? (
                  <WorkflowSyncBlock
                    title={hasWorkflowRecovery ? "Previous Task-State Sync" : "Task State Sync"}
                    sync={workflowTaskState}
                    historical={hasWorkflowRecovery}
                  />
                ) : (
                  <div className="workflow-sync-block is-empty">
                    <strong>Task State Sync</strong>
                    <p>No task-state workflow sync recorded yet.</p>
                  </div>
                )}
                {Object.keys(workflowValidation).length ? (
                  <WorkflowSyncBlock
                    title={hasWorkflowRecovery ? "Previous Validation Sync" : "Validation Sync"}
                    sync={workflowValidation}
                    historical={hasWorkflowRecovery}
                  />
                ) : (
                  <div className="workflow-sync-block is-empty">
                    <strong>Validation Sync</strong>
                    <p>No validation workflow sync recorded yet.</p>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function WorkflowCenter({
  apiBaseUrl,
  getAccessToken,
  userProfile = null,
  routeParams = {},
  setMode,
  themeMode = "light",
}) {
  const { setCopilotContext, clearCopilotContext } = useAdvisor();
  const normalizedBase = useMemo(() => {
    const base = String(apiBaseUrl || "").trim();
    return base.endsWith("/") ? base.slice(0, -1) : base;
  }, [apiBaseUrl]);

  const tenantId = getTenantId(userProfile || {});
  const userId = getUserId(userProfile || {});

  const [summary, setSummary] = useState(null);
  const [workflows, setWorkflows] = useState([]);
  const [selectedWorkflowId, setSelectedWorkflowId] = useState(routeParams?.workflowId || "");
  const [detail, setDetail] = useState(null);
  const [history, setHistory] = useState(null);
  const [linkedTasks, setLinkedTasks] = useState([]);
  const [linkedTasksLoading, setLinkedTasksLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState(routeParams?.status || "all");
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [notice, setNotice] = useState(null);
  const [workflowExplainOpen, setWorkflowExplainOpen] = useState(false);
  const [workflowExplainQuestion, setWorkflowExplainQuestion] = useState(WORKFLOW_EXPLAIN_PROMPTS[0]);
  const [workflowExplainCard, setWorkflowExplainCard] = useState(null);
  const [workflowExplainLoading, setWorkflowExplainLoading] = useState(false);
  const [workflowExplainError, setWorkflowExplainError] = useState("");

  const fetchToken = useCallback(async () => {
    if (typeof getAccessToken !== "function") {
      throw new Error("Workflow Center requires an access token provider.");
    }
    return await getAccessToken();
  }, [getAccessToken]);

  const requestJson = useCallback(
    async (path, options = {}) => {
      const token = await fetchToken();
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        ...(options.headers || {}),
      };

      if (tenantId) headers["X-Tenant-Id"] = tenantId;
      if (userId) headers["X-User-Id"] = userId;

      try {
        const res = await fetch(`${normalizedBase}${path}`, {
          method: options.method || "GET",
          headers,
          body: options.body ? JSON.stringify(options.body) : undefined,
          signal: controller.signal,
        });

        const text = await res.text();
        const data = text ? JSON.parse(text) : null;

        if (!res.ok) {
          const detailMessage = data?.detail || data?.message || text || res.statusText;
          throw new Error(`${res.status} ${res.statusText}: ${detailMessage}`);
        }

        return data;
      } finally {
        clearTimeout(timeoutId);
      }
    },
    [fetchToken, normalizedBase, tenantId, userId]
  );

  const loadOverview = useCallback(async () => {
    setLoading(true);
    setErr(null);

    try {
      const params = new URLSearchParams();
      if (tenantId) params.set("tenant_id", tenantId);

      const workflowParams = new URLSearchParams();
      if (tenantId) workflowParams.set("tenant_id", tenantId);
      if (statusFilter && statusFilter !== "all") workflowParams.set("status", statusFilter);
      workflowParams.set("limit", "100");

      const [summaryPayload, workflowPayload] = await Promise.all([
        requestJson(`/workflows/summary${params.toString() ? `?${params.toString()}` : ""}`),
        requestJson(`/workflows${workflowParams.toString() ? `?${workflowParams.toString()}` : ""}`),
      ]);

      const nextWorkflows = Array.isArray(workflowPayload?.items) ? workflowPayload.items : [];
      setSummary(summaryPayload || null);
      setWorkflows(nextWorkflows);

      setSelectedWorkflowId((current) => {
        if (current && nextWorkflows.some((item) => item.id === current)) return current;
        if (routeParams?.workflowId && nextWorkflows.some((item) => item.id === routeParams.workflowId)) {
          return routeParams.workflowId;
        }
        return nextWorkflows[0]?.id || "";
      });
    } catch (error) {
      console.error("Workflow Center load failed:", error);
      setErr(String(error?.message || error));
    } finally {
      setLoading(false);
    }
  }, [requestJson, routeParams?.workflowId, statusFilter, tenantId]);

  const loadDetail = useCallback(
    async (workflowId) => {
      if (!workflowId) {
        setDetail(null);
        setHistory(null);
        setLinkedTasks([]);
        setLinkedTasksLoading(false);
        return;
      }

      setDetailLoading(true);
      setErr(null);

      try {
        const [detailPayload, historyPayload] = await Promise.all([
          requestJson(`/workflows/${workflowId}`),
          requestJson(`/workflows/${workflowId}/history`),
        ]);
        setDetail(detailPayload || null);
        setHistory(historyPayload || null);

        const remediationLinks = linkedRemediationLinks(detailPayload?.links);
        if (remediationLinks.length === 0) {
          setLinkedTasks([]);
          setLinkedTasksLoading(false);
        } else {
          setLinkedTasksLoading(true);
          const linkedResults = await Promise.all(
            remediationLinks.map(async (link) => {
              try {
                const taskPayload = await requestJson(`/remediation/tasks/${link.linked_id}`);
                return { link, task: normalizeApiTaskPayload(taskPayload), error: null };
              } catch (linkedError) {
                console.warn("Linked remediation task load failed:", linkedError);
                return { link, task: null, error: String(linkedError?.message || linkedError) };
              }
            })
          );
          setLinkedTasks(linkedResults);
          setLinkedTasksLoading(false);
        }
      } catch (error) {
        console.error("Workflow detail load failed:", error);
        setErr(String(error?.message || error));
        setLinkedTasks([]);
        setLinkedTasksLoading(false);
      } finally {
        setDetailLoading(false);
      }
    },
    [requestJson]
  );

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  useEffect(() => {
    loadDetail(selectedWorkflowId);
  }, [loadDetail, selectedWorkflowId]);

  const selectedWorkflow = detail?.workflow || null;

  useEffect(() => {
    const workflowId = String(selectedWorkflow?.id || selectedWorkflowId || "").trim();
    const pageContext = {
      page: "workflow",
      surface: "workflow_page",
      intelligence_mode: workflowId ? "contextual_explain" : "page",
      ...(statusFilter ? { filter: statusFilter } : {}),
    };

    if (!workflowId) {
      clearCopilotContext();
      setCopilotContext(pageContext);
      return;
    }

    setCopilotContext({
      ...pageContext,
      record_type: "workflow",
      workflow_id: workflowId,
      focus_label: String(
        selectedWorkflow?.name ||
          selectedWorkflow?.title ||
          selectedWorkflow?.workflow_name ||
          "Selected workflow"
      ),
      focus_type: "Workflow",
    });
  }, [
    clearCopilotContext,
    selectedWorkflow,
    selectedWorkflowId,
    setCopilotContext,
    statusFilter,
  ]);
  const selectedSteps = useMemo(() => (Array.isArray(detail?.steps) ? detail.steps : []), [detail?.steps]);
  const selectedDependencies = useMemo(
    () => (Array.isArray(detail?.dependencies) ? detail.dependencies : []),
    [detail?.dependencies]
  );
  const selectedApprovals = useMemo(
    () => (Array.isArray(detail?.approvals) ? detail.approvals : []),
    [detail?.approvals]
  );
  const stepHistory = useMemo(
    () => (Array.isArray(history?.step_history) ? history.step_history : []),
    [history?.step_history]
  );
  const executionLog = useMemo(
    () => (Array.isArray(history?.execution_log) ? history.execution_log : []),
    [history?.execution_log]
  );

  const currentStep = useMemo(() => {
    if (!selectedWorkflow?.current_step_id) return null;
    return selectedSteps.find((step) => step.id === selectedWorkflow.current_step_id) || null;
  }, [selectedSteps, selectedWorkflow?.current_step_id]);

  const dependencyHealthLabel = useMemo(() => {
    const pending = safeNumber(summary?.dependency_pending_count);
    const blocked = safeNumber(summary?.dependency_blocked_count);
    const failed = safeNumber(summary?.dependency_failed_count);
    const total = pending + blocked + failed;
    if (total <= 0) return "Clear";
    return `${total} issue${total === 1 ? "" : "s"}`;
  }, [summary]);

  const runWorkflowAction = useCallback(
    async (action, body = {}) => {
      if (!selectedWorkflowId) return;
      setActionLoading(true);
      setNotice(null);
      setErr(null);

      try {
        const result = await requestJson(`/workflows/${selectedWorkflowId}/${action}`, {
          method: "POST",
          body,
        });
        setNotice(result?.message || `Workflow ${action} completed.`);
        await loadOverview();
        await loadDetail(selectedWorkflowId);
      } catch (error) {
        console.error(`Workflow ${action} failed:`, error);
        setErr(String(error?.message || error));
      } finally {
        setActionLoading(false);
      }
    },
    [loadDetail, loadOverview, requestJson, selectedWorkflowId]
  );

  const handleAdvance = useCallback(
    (toStatus) => {
      runWorkflowAction("advance", {
        to_status: toStatus,
        reason: `Workflow Center advance to ${toStatus}`,
        actor_role: "system",
      });
    },
    [runWorkflowAction]
  );

  const handleEvaluateDependencies = useCallback(() => {
    runWorkflowAction("dependencies/evaluate", {
      actor_role: "system",
      auto_update_workflow_status: true,
    });
  }, [runWorkflowAction]);

  const handleApprove = useCallback(() => {
    runWorkflowAction("approve", {
      approval_key: `workflow-center-${Date.now()}`,
      decision: "approved",
      reason: "Approved from Workflow Center.",
      actor_role: "compliance_lead",
    });
  }, [runWorkflowAction]);

  const handleOpenLinkedTask = useCallback(
    ({ task = {}, link = {} } = {}) => {
      if (typeof setMode !== "function") return;

      const taskId = String(task?.id || link?.linked_id || "").trim();
      if (!taskId) return;

      const taskMetadata = metadataDict(task?.task_metadata);
      const workflowMetadata = metadataDict(taskMetadata.workflow);
      const workflowId =
        workflowMetadata.workflow_id ||
        workflowMetadata.workflowId ||
        workflowMetadata.workflow_instance_id ||
        selectedWorkflowId ||
        "";

      setMode("posture", {
        framework: task?.framework || selectedWorkflow?.framework || "SOC2",
        controlId: task?.control_id || task?.controlId || selectedWorkflow?.control_id || undefined,
        ruleId: task?.rule_id || task?.ruleId || selectedWorkflow?.rule_id || undefined,
        resourceId: task?.resource_id || task?.resourceId || selectedWorkflow?.resource_id || undefined,
        remediationTaskId: taskId,
        openTaskId: taskId,
        selectedTaskId: taskId,
        taskId,
        targetSection: "remediation_task",
        section: "remediation_task",
        returnMode: "workflows",
        returnTo: "workflow_center",
        source: "workflow_center",
        workflowId,
        routeNavNonce: String(Date.now()),
      });
    },
    [selectedWorkflow, selectedWorkflowId, setMode]
  );

  const askWorkflowExplain = useCallback(
    async (question) => {
      if (!selectedWorkflow?.id) return;

      setWorkflowExplainQuestion(question);
      setWorkflowExplainLoading(true);
      setWorkflowExplainError("");

      try {
        const payload = await requestJson("/ai/copilot/ask", {
          method: "POST",
          body: {
            scope: "workflow",
            question,
            copilot_context: {
              page: "workflow_center",
              surface: "workflow_page",
              intelligence_mode: "contextual_explain",
              record_type: "workflow",
              workflow_id: selectedWorkflow.id,
              workflow_status: selectedWorkflow.status || undefined,
              current_step_id: currentStep?.id || selectedWorkflow.current_step_id || undefined,
              current_step_key: currentStep?.step_key || undefined,
              framework: selectedWorkflow.framework || undefined,
              control_id: selectedWorkflow.control_id || undefined,
              rule_id: selectedWorkflow.rule_id || undefined,
              resource_id: selectedWorkflow.resource_id || undefined,
              source: selectedWorkflow.source || undefined,
              stream: selectedWorkflow.stream || undefined,
            },
          },
        });

        if (!payload?.card || typeof payload.card !== "object") {
          throw new Error("Workflow Intelligence returned no grounded explanation card.");
        }

        setWorkflowExplainCard(payload.card);
      } catch (error) {
        console.error("Workflow contextual explain failed:", error);
        setWorkflowExplainError(String(error?.message || error || "Unable to explain this workflow."));
      } finally {
        setWorkflowExplainLoading(false);
      }
    },
    [currentStep, requestJson, selectedWorkflow]
  );

  const openWorkflowExplain = useCallback(() => {
    if (!selectedWorkflow?.id) return;
    setWorkflowExplainOpen(true);
    setWorkflowExplainCard(null);
    setWorkflowExplainError("");
    setWorkflowExplainQuestion(WORKFLOW_EXPLAIN_PROMPTS[0]);
    askWorkflowExplain(WORKFLOW_EXPLAIN_PROMPTS[0]);
  }, [askWorkflowExplain, selectedWorkflow?.id]);

  const closeWorkflowExplain = useCallback(() => {
    setWorkflowExplainOpen(false);
  }, []);

  const statusOptions = [
    "all",
    "queued",
    "assigned",
    "executing",
    "awaiting_approval",
    "awaiting_validation",
    "blocked",
    "completed",
    "failed",
  ];

  return (
    <div className={`workflow-center ${themeMode === "dark" ? "dark" : "light"}`}>
      <div className="workflow-center-shell">
        <header className="workflow-center-hero workflow-page-header">
          <div className="workflow-page-header-copy">
            <h1>Workflow Center</h1>
            <p>
              Monitor autonomous workflow execution, dependency health, approvals,
              validation readiness, and operational bottlenecks.
            </p>
          </div>
          <div className="workflow-center-actions workflow-page-header-actions">
            <button type="button" onClick={() => setMode?.("dashboard")}>
              Mission Control
            </button>
            <button type="button" onClick={loadOverview} disabled={loading || actionLoading}>
              Refresh
            </button>
          </div>
        </header>

        {err ? <div className="workflow-alert is-danger">{err}</div> : null}
        {notice ? <div className="workflow-alert is-ok">{notice}</div> : null}

        <WorkflowIntelligencePanel
          requestJson={requestJson}
          themeMode={themeMode}
        />

        <section className="workflow-summary-grid">
          <StatCard
            label="Total Workflows"
            value={safeNumber(summary?.total)}
            footnote={`${safeNumber(summary?.created_today)} created today`}
          />
          <StatCard
            label="Running"
            value={safeNumber(summary?.running)}
            tone="ok"
            footnote={`${safeNumber(summary?.queued)} queued`}
          />
          <StatCard
            label="Blocked"
            value={safeNumber(summary?.blocked)}
            tone={safeNumber(summary?.blocked) > 0 ? "danger" : "ok"}
            footnote="active blockers"
          />
          <StatCard
            label="Approvals"
            value={safeNumber(summary?.awaiting_approval)}
            tone={safeNumber(summary?.awaiting_approval) > 0 ? "warn" : "ok"}
            footnote="awaiting approval"
          />
          <StatCard
            label="Validation"
            value={safeNumber(summary?.awaiting_validation)}
            tone={safeNumber(summary?.awaiting_validation) > 0 ? "warn" : "ok"}
            footnote="awaiting validation"
          />
          <StatCard
            label="Dependency Health"
            value={dependencyHealthLabel}
            tone={dependencyHealthLabel === "Clear" ? "ok" : "warn"}
            footnote="pending / blocked / failed"
          />
        </section>

        <section className="workflow-workspace-stack">
          <section className="workflow-list-panel workflow-queue-panel">
            <div className="workflow-panel-header">
              <div>
                <h2>Active Workflows</h2>
                <p>{loading ? "Loading…" : `${workflows.length} workflow${workflows.length === 1 ? "" : "s"}`}</p>
              </div>
              <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                {statusOptions.map((status) => (
                  <option key={status} value={status}>
                    {status === "all" ? "All statuses" : formatStatus(status)}
                  </option>
                ))}
              </select>
            </div>

            <div className="workflow-list">
              {workflows.length === 0 && !loading ? (
                <EmptyState
                  title="No workflows found"
                  description="Workflow instances will appear here as findings and events are orchestrated."
                />
              ) : null}

              {workflows.map((workflow) => (
                <button
                  key={workflow.id}
                  type="button"
                  className={`workflow-list-item ${workflow.id === selectedWorkflowId ? "active" : ""}`}
                  onClick={() => setSelectedWorkflowId(workflow.id)}
                >
                  <div>
                    <strong>{workflow.name || workflow.rule_id || "Workflow"}</strong>
                    <span>
                      {formatSource(workflow.source)} · {workflow.resource_id || "resource"}
                    </span>
                  </div>
                  <StatusBadge value={workflow.status} />
                </button>
              ))}
            </div>
          </section>

          <section className="workflow-detail-panel workflow-selected-panel">
            {!selectedWorkflow && !detailLoading ? (
              <EmptyState
                title="Select a workflow"
                description="Choose a workflow to view steps, dependencies, approvals, and execution history."
              />
            ) : null}

            {selectedWorkflow ? (
              <>
                <div className="workflow-detail-header">
                  <div>
                    <p className="workflow-center-eyebrow">{selectedWorkflow.template_slug}</p>
                    <h2>{selectedWorkflow.name}</h2>
                    <p>{selectedWorkflow.description || "No workflow description provided."}</p>
                  </div>
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      gap: 8,
                      flexWrap: "wrap",
                    }}
                  >
                    <StatusBadge value={selectedWorkflow.status} />
                    <Button
                      variant="ghost"
                      size="small"
                      onClick={openWorkflowExplain}
                    >
                      Explain
                    </Button>
                  </div>
                </div>

                <div className="workflow-current-state">
                  <div className="workflow-current-state-grid">
                    <span>
                      Source <strong>{formatSource(selectedWorkflow.source)}</strong>
                    </span>
                    <span>
                      Rule <strong>{selectedWorkflow.rule_id || "—"}</strong>
                    </span>
                    <span>
                      Control <strong>{selectedWorkflow.control_id || "—"}</strong>
                    </span>
                    <span>
                      Current step <strong>{currentStep?.name || "—"}</strong>
                    </span>
                  </div>

                  <div className="workflow-action-row">
                    <div className="workflow-action-group workflow-action-group-primary">
                      <button type="button" disabled={actionLoading} onClick={() => handleAdvance("assigned")}>
                        Assign
                      </button>
                      <button type="button" disabled={actionLoading} onClick={() => handleAdvance("executing")}>
                        Execute
                      </button>
                      <button
                        type="button"
                        disabled={actionLoading}
                        onClick={() => handleAdvance("awaiting_validation")}
                      >
                        Await Validation
                      </button>
                      <button type="button" disabled={actionLoading} onClick={() => handleAdvance("completed")}>
                        Complete Step
                      </button>
                    </div>

                    <div className="workflow-action-group workflow-action-group-secondary">
                      <button type="button" disabled={actionLoading} onClick={handleEvaluateDependencies}>
                        Evaluate Dependencies
                      </button>
                      <button type="button" disabled={actionLoading} onClick={handleApprove}>
                        Approve
                      </button>
                    </div>
                  </div>
                </div>

                <div className="workflow-detail-grid">
                  <LinkedWorkPanel linkedTasks={linkedTasks} loading={linkedTasksLoading} onOpenLinkedTask={handleOpenLinkedTask} />

                  <div className="workflow-detail-column workflow-detail-column-main">
                    <div className="workflow-card-block workflow-execution-steps-card">
                      <h3>Execution Steps</h3>
                      <div className="workflow-timeline">
                        {selectedSteps.map((step) => (
                          <div
                            key={step.id}
                            className={`workflow-timeline-item ${
                              step.id === selectedWorkflow.current_step_id ? "current" : ""
                            }`}
                          >
                            <div className="workflow-timeline-dot" />
                            <div>
                              <strong>{step.name}</strong>
                              <span>
                                {step.step_key} · {step.step_type}
                              </span>
                            </div>
                            <StatusBadge value={step.status} />
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="workflow-card-block workflow-approvals-card">
                      <h3>Approvals</h3>
                      <div className="workflow-rows">
                        {selectedApprovals.length === 0 ? (
                          <p className="workflow-muted">No approvals recorded.</p>
                        ) : null}
                        {selectedApprovals.map((approval) => (
                          <div key={approval.id} className="workflow-row-card">
                            <div>
                              <strong>{approval.approval_key}</strong>
                              <span>{approval.required_role || approval.approver_role || "approval role"}</span>
                            </div>
                            <StatusBadge value={approval.status} />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="workflow-detail-column workflow-detail-column-side">
                    <div className="workflow-card-block workflow-dependencies-card">
                      <h3>Dependencies</h3>
                      <div className="workflow-rows">
                        {selectedDependencies.length === 0 ? (
                          <p className="workflow-muted">No dependencies recorded.</p>
                        ) : null}
                        {selectedDependencies.map((dependency) => (
                          <div key={dependency.id} className="workflow-row-card">
                            <div>
                              <strong>{dependency.dependency_type}</strong>
                              <span>
                                {dependency.dependency_key} · requires {dependency.required_state || "state"}
                              </span>
                            </div>
                            <StatusBadge value={dependency.status} />
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="workflow-card-block workflow-history-card">
                      <h3>Execution History</h3>
                      <div className="workflow-history-list">
                        {[...stepHistory, ...executionLog]
                          .sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0))
                          .slice(-10)
                          .map((item) => {
                            const historyTone = workflowHistoryTone(item);

                            return (
                              <div
                                key={`${item.id}-${item.event_type || item.to_status}`}
                                className="workflow-history-item"
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    gap: 10,
                                    alignItems: "center",
                                    flexWrap: "wrap",
                                  }}
                                >
                                  <strong>{formatWorkflowHistoryTitle(item)}</strong>
                                  <span style={syncBadgeStyle(historyTone)}>{formatStatus(historyTone)}</span>
                                </div>
                                <span>{formatWorkflowHistoryDetail(item)}</span>
                                <small>{formatDateTime(item.created_at)}</small>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  </div>
                </div>
              </>
            ) : null}
          </section>
        </section>

        <ContextualExplainDrawer
          open={workflowExplainOpen}
          record={selectedWorkflow}
          recordLabel={selectedWorkflow?.name || selectedWorkflow?.rule_id || "Selected workflow"}
          question={workflowExplainQuestion}
          card={workflowExplainCard}
          metadata={workflowExplainCard?.metadata || null}
          loading={workflowExplainLoading}
          error={workflowExplainError}
          themeMode={themeMode}
          prompts={WORKFLOW_EXPLAIN_PROMPTS}
          contextDescription="Grounded reasoning for this selected workflow. Deterministic workflow state, dependencies, approvals, validation, history, and governed workflow controls remain authoritative."
          onAsk={askWorkflowExplain}
          onClose={closeWorkflowExplain}
        />
      </div>
    </div>
  );
} 